import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useConfirm } from "components/admin/Feedback";
import useConfirmChanges from "components/admin/confirmChanges";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

function SwitchRow({ checked, onChange, disabled, label, help }) {
  return (
    <MDBox>
      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Switch
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={
          <MDTypography variant="button" fontWeight="medium">
            {label}
          </MDTypography>
        }
      />
      {help && (
        <MDTypography variant="caption" color="text" display="block" ml={6}>
          {help}
        </MDTypography>
      )}
    </MDBox>
  );
}

SwitchRow.defaultProps = { disabled: false, help: null };
SwitchRow.propTypes = {
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  help: PropTypes.string,
};

const PLATFORM_NAMES = {
  android: "Android (APK)",
  windows: "Windows (EXE)",
  mac: "macOS",
  jar: "Java (JAR)",
};

function ServerSettings() {
  const confirm = useConfirm();
  const ask = useConfirmChanges();
  const [run, busy] = useRunner();
  const canEdit = useCan(PERMISSIONS.SERVER_CONTROL);
  const settings = useFetch(() => api.get("/api/admin/server/settings"), []);
  const s = settings.data;

  const [login, setLogin] = useState(null);
  const [client, setClient] = useState(null);
  const [rates, setRates] = useState(null);
  const [allowIps, setAllowIps] = useState("");

  useEffect(() => {
    if (!s) return;
    setLogin({
      isOnlyAdminLogin: s.isOnlyAdminLogin,
      isShowMessageWhenLogin: s.isShowMessageWhenLogin,
      messageWhenLogin: s.messageWhenLogin,
      kickNonAdmin: false,
    });
    setClient({
      minClientVersion: s.minClientVersion,
      clientUpdateUrl: s.clientUpdateUrl,
      clientUpdateMessage: s.clientUpdateMessage,
      clientVersions: s.clientVersions || [],
    });
    setRates({ expPercent: s.expPercent, gemPercent: s.gemPercent });
    setAllowIps((s.adminAllowIps || []).join("\n"));
  }, [s]);

  const saveLogin = async () => {
    const yes = await ask({
      title: "Lưu cài đặt đăng nhập",
      before: { ...s, kickNonAdmin: false },
      after: login,
      fields: [
        ["isOnlyAdminLogin", "Chỉ nhân vật admin được vào game"],
        ["isShowMessageWhenLogin", "Chặn mọi lượt đăng nhập / đăng ký"],
        ["messageWhenLogin", "Lời nhắn khi chặn"],
        ["kickNonAdmin", "Đá người không phải admin đang online"],
      ],
      notes: [
        login.isOnlyAdminLogin &&
          !s.isOnlyAdminLogin &&
          "⚠ Người chơi thường sẽ KHÔNG đăng nhập được nữa cho tới khi tắt lại.",
        login.isShowMessageWhenLogin &&
          !s.isShowMessageWhenLogin &&
          "⚠ KHÔNG AI vào được game (kể cả nhân vật admin) — người chơi chỉ thấy lời nhắn. Web admin vẫn vào được.",
        login.kickNonAdmin &&
          '⚠ Mọi người chơi không phải admin đang online thấy thông báo "máy chủ chỉ dành cho admin" rồi bị ngắt kết nối NGAY.',
      ],
      confirmText: login.kickNonAdmin ? "Lưu và đá người chơi" : "Lưu",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put("/api/admin/server/settings/login", login),
      (data) => (data.kicked > 0 ? `Đã lưu, kick ${data.kicked} người` : "Đã lưu")
    );
    if (ok) settings.reload(true);
  };

  const saveAdminIps = async () => {
    const ips = allowIps
      .split(/[\s,;]+/)
      .map((ip) => ip.trim())
      .filter(Boolean);
    const yes = await confirm({
      title: "Lưu danh sách IP được vào web admin",
      message:
        ips.length === 0
          ? "Bỏ giới hạn: mọi IP đều mở được trang đăng nhập web admin (vẫn cần mật khẩu + mã 2 lớp)."
          : [
              `Chỉ ${ips.length} IP này mở được web admin:`,
              ...ips.map((ip) => `• ${ip}${ip === s.clientIp ? " (máy bạn đang dùng)" : ""}`),
              "",
              "Admin dùng IP khác (đổi mạng, 4G...) sẽ không vào được cho tới khi thêm IP.",
              ips.includes(s.clientIp)
                ? ""
                : `⚠ Thiếu IP máy bạn (${s.clientIp || "không rõ"}) — máy chủ sẽ từ chối lưu.`,
            ].join("\n"),
      color: "warning",
      confirmText: "Lưu",
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put("/api/admin/server/settings/admin-ips", { ips }),
      (data) => (data.ips.length === 0 ? "Đã bỏ giới hạn IP" : `Đã lưu ${data.ips.length} IP`)
    );
    if (ok) settings.reload(true);
  };

  const setPlatform = (platform, field, value) =>
    setClient({
      ...client,
      clientVersions: client.clientVersions.map((p) =>
        p.platform === platform ? { ...p, [field]: value } : p
      ),
    });

  const saveClient = async () => {
    const raised = Number(client.minClientVersion) > Number(s.minClientVersion);
    const yes = await ask({
      title: "Lưu phiên bản client",
      before: { ...s, clientVersions: s.clientVersions || [] },
      after: client,
      fields: [
        ["minClientVersion", "Phiên bản tối thiểu"],
        ["clientUpdateUrl", "Link tải bản mới"],
        ["clientUpdateMessage", "Thông báo cập nhật"],
        ["clientVersions", "Phiên bản từng nền tảng"],
      ],
      notes: [
        raised &&
          "⚠ Nâng phiên bản tối thiểu: người dùng bản cũ hơn sẽ KHÔNG vào được game cho tới khi tải bản mới. Kiểm tra link tải đã có bản mới chưa.",
      ],
    });
    if (!yes) return;
    const { ok } = await run(
      () => api.put("/api/admin/server/settings/client-version", client),
      (v) => `Phiên bản tối thiểu: ${v}`
    );
    if (ok) settings.reload(true);
  };

  const saveRates = async () => {
    const yes = await ask({
      title: "Lưu tỉ lệ exp / ngọc",
      before: s,
      after: rates,
      fields: [
        ["expPercent", "Tỉ lệ exp", { text: (v) => `${v}%` }],
        ["gemPercent", "Tỉ lệ ngọc", { text: (v) => `${v}%` }],
      ],
      notes: ["Áp dụng ngay cho mọi người chơi."],
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.put("/api/admin/server/settings/rates", {
          expPercent: Number(rates.expPercent),
          gemPercent: Number(rates.gemPercent),
        }),
      (data) => `Exp ${data.expPercent}%, ngọc ${data.gemPercent}%`
    );
    if (ok) settings.reload(true);
  };

  const cancelMaintenance = async () => {
    if (
      !(await confirm({
        title: "Huỷ bảo trì",
        message: "Dừng đếm ngược bảo trì và báo cho người chơi?",
        color: "warning",
        confirmText: "Huỷ bảo trì",
      }))
    )
      return;
    const { ok } = await run(
      () => api.post("/api/admin/server/maintenance/cancel"),
      (m) => m
    );
    if (ok) settings.reload(true);
  };

  const saveAll = async () => {
    const { ok } = await run(
      () => api.post("/api/admin/server/save-all"),
      (d) =>
        `Đã lưu ${d.players} người chơi, ${d.clans} bang` + (d.errors ? `, ${d.errors} lỗi` : "")
    );
    if (ok) settings.reload(true);
  };

  const backup = async () => {
    const { ok } = await run(
      () => api.post("/api/admin/server/backup"),
      (m) => m
    );
    if (ok) settings.reload(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="server-settings" />
        {settings.error && <Alert severity="error">{settings.error}</Alert>}
        {s && login && client && rates && (
          <Grid container spacing={3}>
            {s.maintenance.active && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  action={
                    canEdit && (
                      <MDButton
                        size="small"
                        color="warning"
                        disabled={busy}
                        onClick={cancelMaintenance}
                      >
                        Huỷ bảo trì
                      </MDButton>
                    )
                  }
                >
                  Đang đếm ngược bảo trì, còn khoảng {s.maintenance.minutesLeft} phút, hết giờ máy
                  chủ sẽ {s.maintenance.restart ? "khởi động lại" : "tắt hẳn"}.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} lg={6}>
              <PageCard
                title="Đăng nhập game"
                subtitle="Lưu vào config/server.json, có hiệu lực ngay"
              >
                <MDBox display="flex" flexDirection="column" gap={1.5}>
                  <SwitchRow
                    checked={login.isOnlyAdminLogin}
                    disabled={!canEdit}
                    onChange={(v) => setLogin({ ...login, isOnlyAdminLogin: v })}
                    label="Chỉ nhân vật admin được vào game"
                    help="Người khác đăng nhập sẽ bị báo máy chủ chỉ dành cho admin rồi ngắt kết nối."
                  />
                  {login.isOnlyAdminLogin && !s.isOnlyAdminLogin && (
                    <SwitchRow
                      checked={login.kickNonAdmin}
                      disabled={!canEdit}
                      onChange={(v) => setLogin({ ...login, kickNonAdmin: v })}
                      label="Kick luôn người không phải admin đang online"
                    />
                  )}
                  <SwitchRow
                    checked={login.isShowMessageWhenLogin}
                    disabled={!canEdit}
                    onChange={(v) => setLogin({ ...login, isShowMessageWhenLogin: v })}
                    label="Chặn mọi lượt đăng nhập / đăng ký và hiện lời nhắn"
                    help="Chặn cả nhân vật admin trong game. Web admin vẫn đăng nhập bình thường."
                  />
                  <MDInput
                    label="Lời nhắn khi chặn"
                    value={login.messageWhenLogin}
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(e) => setLogin({ ...login, messageWhenLogin: e.target.value })}
                  />
                  {canEdit && (
                    <MDBox>
                      <MDButton variant="gradient" color="info" disabled={busy} onClick={saveLogin}>
                        <Icon>save</Icon>&nbsp;Lưu
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>
              </PageCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <PageCard
                title="IP được vào web admin"
                subtitle="Chỉ những IP này mở được trang admin. Có hiệu lực ngay"
              >
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <MDTypography variant="button" color="text">
                    IP máy bạn đang dùng: {s.clientIp || "không xác định"}
                  </MDTypography>
                  <MDInput
                    label="Mỗi IP một dòng"
                    value={allowIps}
                    multiline
                    minRows={3}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(e) => setAllowIps(e.target.value)}
                  />
                  <MDTypography variant="caption" color="text">
                    Để trống là máy nào cũng mở được trang đăng nhập. Danh sách có IP thì phải còn
                    IP máy này, nếu không sẽ không lưu. Mất hết máy tính thì từ máy mới SSH vào VPS
                    và chạy petrealm-admin-ip, lệnh đó thêm IP của phiên SSH vào danh sách.
                  </MDTypography>
                  {canEdit && (
                    <MDBox>
                      <MDButton
                        variant="gradient"
                        color="info"
                        disabled={busy}
                        onClick={saveAdminIps}
                      >
                        <Icon>save</Icon>&nbsp;Lưu
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>
              </PageCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <PageCard
                title="Phiên bản client"
                subtitle="Client cũ hơn bản tối thiểu sẽ bị chặn và nhắc tải bản mới"
              >
                <MDBox display="flex" flexDirection="column" gap={2}>
                  {client.clientVersions.map((p) => (
                    <MDBox key={p.platform} display="flex" flexDirection="column" gap={1}>
                      <MDTypography variant="button" fontWeight="medium">
                        {PLATFORM_NAMES[p.platform] || p.platform}
                      </MDTypography>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={4}>
                          <MDInput
                            fullWidth
                            label="Tối thiểu"
                            placeholder={client.minClientVersion}
                            value={p.min}
                            InputProps={{ readOnly: !canEdit }}
                            onChange={(e) => setPlatform(p.platform, "min", e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <MDInput
                            fullWidth
                            label="Link tải"
                            placeholder={client.clientUpdateUrl}
                            value={p.url}
                            InputProps={{ readOnly: !canEdit }}
                            onChange={(e) => setPlatform(p.platform, "url", e.target.value)}
                          />
                        </Grid>
                      </Grid>
                      <MDInput
                        label="Lời nhắn"
                        placeholder={client.clientUpdateMessage}
                        value={p.message}
                        multiline
                        InputProps={{ readOnly: !canEdit }}
                        onChange={(e) => setPlatform(p.platform, "message", e.target.value)}
                      />
                    </MDBox>
                  ))}
                  <MDTypography variant="caption" color="text">
                    Ô nào của từng bản để trống thì dùng mục mặc định bên dưới. Mục mặc định cũng áp
                    cho bản không có trong danh sách. {"{version}"} trong lời nhắn được thay bằng
                    phiên bản tối thiểu của bản đó.
                  </MDTypography>
                  <MDTypography variant="button" fontWeight="medium">
                    Mặc định
                  </MDTypography>
                  <MDInput
                    label="Phiên bản tối thiểu (ví dụ 1.4.2)"
                    value={client.minClientVersion}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(e) => setClient({ ...client, minClientVersion: e.target.value })}
                  />
                  <MDInput
                    label="Link tải bản mới (để trống thì chỉ hiện lời nhắn)"
                    value={client.clientUpdateUrl}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(e) => setClient({ ...client, clientUpdateUrl: e.target.value })}
                  />
                  <MDInput
                    label="Lời nhắn (để trống thì dùng câu mặc định)"
                    value={client.clientUpdateMessage}
                    multiline
                    minRows={2}
                    InputProps={{ readOnly: !canEdit }}
                    onChange={(e) => setClient({ ...client, clientUpdateMessage: e.target.value })}
                  />
                  {canEdit && (
                    <MDBox>
                      <MDButton
                        variant="gradient"
                        color="info"
                        disabled={busy || !client.minClientVersion.trim()}
                        onClick={saveClient}
                      >
                        <Icon>save</Icon>&nbsp;Lưu
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>
              </PageCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <PageCard
                title="Hệ số exp / ngọc"
                subtitle="Nhân với exp và ngọc nhận được khi đánh quái, 100% là bình thường"
                color="success"
              >
                <MDBox display="flex" flexDirection="column" gap={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <MDInput
                        fullWidth
                        type="number"
                        label="Exp (%)"
                        value={rates.expPercent}
                        InputProps={{ readOnly: !canEdit }}
                        onChange={(e) => setRates({ ...rates, expPercent: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MDInput
                        fullWidth
                        type="number"
                        label="Ngọc (%)"
                        value={rates.gemPercent}
                        InputProps={{ readOnly: !canEdit }}
                        onChange={(e) => setRates({ ...rates, gemPercent: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                  {canEdit && (
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                      <MDButton variant="gradient" color="info" disabled={busy} onClick={saveRates}>
                        <Icon>save</Icon>&nbsp;Lưu
                      </MDButton>
                      {[100, 200, 300].map((v) => (
                        <MDButton
                          key={v}
                          variant="outlined"
                          color="success"
                          size="small"
                          onClick={() => setRates({ expPercent: v, gemPercent: v })}
                        >
                          x{v / 100}
                        </MDButton>
                      ))}
                    </MDBox>
                  )}
                </MDBox>
              </PageCard>
            </Grid>
            <Grid item xs={12} lg={6}>
              <PageCard title="Lưu & sao lưu" color="dark">
                <MDBox display="flex" flexDirection="column" gap={1}>
                  <MDTypography variant="button" color="text">
                    Lần tự lưu kế tiếp: người chơi {formatDate(s.nextSave.players)}, bang hội{" "}
                    {formatDate(s.nextSave.clans)}, ki ốt {formatDate(s.nextSave.market)}
                  </MDTypography>
                  {canEdit && (
                    <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                      <MDButton variant="gradient" color="info" disabled={busy} onClick={saveAll}>
                        <Icon>save</Icon>&nbsp;Lưu tất cả ngay
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="info"
                        disabled={busy || s.backupRunning}
                        onClick={backup}
                      >
                        <Icon>backup</Icon>&nbsp;
                        {s.backupRunning ? "Đang backup..." : "Backup cơ sở dữ liệu"}
                      </MDButton>
                    </MDBox>
                  )}
                  <MDBox mx={-2}>
                    <AdminTable
                      rows={s.backups}
                      rowKey="name"
                      emptyText="Chưa có bản backup"
                      columns={[
                        { key: "name", label: "File trong backup_sql" },
                        {
                          key: "sizeKb",
                          label: "Dung lượng",
                          align: "right",
                          render: (b) => `${formatNumber(b.sizeKb)} KB`,
                        },
                        { key: "time", label: "Lúc", render: (b) => formatDate(b.time) },
                      ]}
                    />
                  </MDBox>
                </MDBox>
              </PageCard>
            </Grid>
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ServerSettings;
